/**
 * The import hub's rules, kept pure so they are tested without a browser: what a job's status means to a
 * plant person, what they may do with it, what an upload's answer says, which equipment an approval must
 * bind (mirroring the server's `manifestStream.equipmentKeys`), where the org's analysis stands, and how a
 * link reaches the hub's readers.
 *
 * The documents lane (migration 0228): a document family's file goes to the family's product, not to import. The hub
 * says where it went, and what a held one waits for, in words a plant person reads.
 */
import {
  isRoutedUpload,
  type ImportJob,
  type ImportJobStatus,
  type ImportApproveRequest,
  type ImportUploadAnswer,
} from "@/lib/api/imports";
import type { AnalysisJob } from "@/lib/api/analysis-jobs";
import { ApiClientError } from "@/lib/api-client";
import { FOUNDRY_SCOPE } from "@/lib/suite";
import { own } from "./own";

/** The hub's readers section, and the link to it (from the conversation's "Review and approve"). */
export const READERS_SECTION_ID = "import-readers";
export const READERS_HREF = `/app/twin/data-sources#${READERS_SECTION_ID}`;

/** The id of the element a location's hash names ("#import-readers" gives "import-readers"), or null for none. */
export function hashTarget(hash: string): string | null {
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw; // a stray "%" is not an escape: the hash as it is
  }
}

/**
 * Brings the element with this id into view (#843 review N4): BrowserRouter changes the hash of a link but never
 * scrolls to it, so a link to a section of the hub, and the hub opened with one in its address, scroll themselves.
 * False when there is no such element (yet), or no document.
 */
export function scrollToSection(
  doc: { getElementById(id: string): { scrollIntoView(options?: ScrollIntoViewOptions): void } | null } | undefined,
  id: string,
): boolean {
  const el = doc?.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ block: "start" });
  return true;
}

const IN_FLIGHT: ReadonlySet<ImportJobStatus> = new Set<ImportJobStatus>([
  "queued",
  "ready",
  "applying",
  "awaiting_author",
  "validating",
  "awaiting_approval",
]);

const CANCELLABLE: ReadonlySet<ImportJobStatus> = new Set<ImportJobStatus>([
  "queued",
  "ready",
  "awaiting_author",
  "validating",
  "awaiting_approval",
  "held",
]);

export function jobInFlight(status: ImportJobStatus): boolean {
  return IN_FLIGHT.has(status);
}

/** A job may be cancelled while it waits, never while a runner is applying it or once it finished. */
export function canCancelJob(status: ImportJobStatus): boolean {
  return CANCELLABLE.has(status);
}

/**
 * A failed or cancelled job may start again from the probe: how a person releases a held file. So may a job held
 * for a reader of its database format, once that reader exists. A held job is not in flight: it waits, so the list
 * does not poll for it.
 */
export function canReprocessJob(status: ImportJobStatus): boolean {
  return status === "failed" || status === "cancelled" || status === "held";
}

const STATUS_TEXT: Record<ImportJobStatus, string> = {
  queued: "Waiting to be read",
  ready: "Recognised; waiting to import",
  applying: "Importing",
  done: "Imported",
  duplicate: "Already imported",
  awaiting_author: "No approved reader for this kind of file yet: one is being written",
  validating: "Checking a new reader on this file",
  awaiting_approval: "A new reader for this file waits for approval",
  failed: "Failed",
  cancelled: "Cancelled",
  held: "Kept: a database file no reader exists for yet. Reprocess it once one does",
};

export function jobStatusText(status: ImportJobStatus): string {
  return STATUS_TEXT[status] ?? status;
}

export type BadgeVariant = "default" | "secondary" | "success" | "warning" | "danger" | "info" | "outline";

const STATUS_BADGE: Record<ImportJobStatus, BadgeVariant> = {
  queued: "default",
  ready: "info",
  applying: "info",
  done: "success",
  duplicate: "secondary",
  awaiting_author: "warning",
  validating: "info",
  awaiting_approval: "warning",
  failed: "danger",
  cancelled: "secondary",
  held: "secondary",
};

export function jobBadge(status: ImportJobStatus): BadgeVariant {
  return STATUS_BADGE[status] ?? "default";
}

// ── The documents lane (migration 0228) ──────────────────────────────────────────────────────────────────────────

/**
 * The names of the suite products a document family's file goes to, by key. A hold's reason names its product by key
 * alone, and a job whose file was sent names no product at all, so their words come from here; an upload's answer
 * carries the product's own name. Every family goes to Foundry Scope today (the server's docfamily.Families).
 */
const PRODUCT_NAMES: Readonly<Record<string, string>> = { [FOUNDRY_SCOPE]: "Foundry Scope" };

/** Where a job's sent file went: the job does not name the product, and Foundry Scope reads every family today. */
const ROUTED_PRODUCT = PRODUCT_NAMES[FOUNDRY_SCOPE];

/** A sentence's words after a file's name in a toast ("report.docx: waiting for ..."). */
function lowerFirst(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

/** Whether a job's file went to another product instead of import: the job was cancelled naming the document. */
export function jobRouted(job: Pick<ImportJob, "routed_document_id">): boolean {
  return !!job.routed_document_id;
}

// The reason the server writes, documents.HoldReason: "document family <family>: product <product> not enabled".
const HOLD_REASON = /^document family [a-z][a-z0-9_]*: product ([a-z][a-z0-9_]*) not enabled$/;

/**
 * A hold's reason in plain words: a document family's file waits for its product to be enabled for the org. A reason
 * in another form, or naming a product with no name here, is shown as it is.
 */
export function holdReasonText(reason: string): string {
  const m = HOLD_REASON.exec(reason.trim());
  const name: string | undefined = m ? PRODUCT_NAMES[m[1]] : undefined;
  return name ? `Waiting for ${name} to be enabled for this organisation` : reason;
}

/** A job's status in words: a sent file says where it went, and a hold with a reason says what it waits for. */
export function jobStateText(job: ImportJob): string {
  if (jobRouted(job)) return `Sent to ${ROUTED_PRODUCT}`;
  if (job.status === "held" && job.hold_reason) return holdReasonText(job.hold_reason);
  return jobStatusText(job.status);
}

/** A date as the plant saw it, "12 Oct 2026": on the plant's zone, or the UTC date where it set none (or one that does
 *  not read). As the server writes the same date in its refusal (standingWords). */
export function plantDateOf(iso: string, zone: string | null | undefined): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const opts = { day: "numeric", month: "short", year: "numeric" } as const;
  try {
    return new Intl.DateTimeFormat("en-GB", { ...opts, timeZone: zone?.trim() || "UTC" }).format(d);
  } catch {
    return new Intl.DateTimeFormat("en-GB", { ...opts, timeZone: "UTC" }).format(d);
  }
}

/** A failed or cancelled job whose file another job has since imported (replaced_by, done): it reads as replaced, not
 *  failed (plan 2026-10-23_cwc-pour-model-and-fixes, PR8). */
export function jobReplaced(job: Pick<ImportJob, "replaced_by">): boolean {
  return job.replaced_by?.status === "done";
}

/**
 * What a failed or cancelled job says of the job that stands in its place, its date written by `when`: imported by it,
 * or being read by it. "" when there is none, or when it is the job's own reader's revision, whose words say it
 * (revisionWords: "Imported by the updated reader.").
 */
export function replacedWords(
  job: Pick<ImportJob, "replaced_by" | "revision_job_id" | "created_at">,
  when: (iso: string) => string,
): string {
  const by = job.replaced_by;
  if (!by || by.id === job.revision_job_id) return "";
  const which = Date.parse(by.at) >= Date.parse(job.created_at) ? "a later" : "another";
  return by.status === "done"
    ? `Replaced by ${which} import of this file, ${when(by.at)}.`
    : `${which === "a later" ? "A later" : "Another"} job of this file is reading it, since ${when(by.at)}.`;
}

/** The badge's word for a job: a sent file's job is cancelled, but no person cancelled it. */
export function jobBadgeText(job: ImportJob): string {
  if (jobRouted(job)) return "sent";
  return jobReplaced(job) ? "replaced" : job.status.replace(/_/g, " ");
}

export function jobBadgeOf(job: ImportJob): BadgeVariant {
  if (jobRouted(job)) return "info";
  return jobReplaced(job) ? "secondary" : jobBadge(job.status);
}

/**
 * How a plant agent's file reached the cloud, in two words: "Direct", or "Via Gateway 7E1A90A1" (the gateway's name;
 * "Via a gateway" when the API names none, e.g. the gateway was deleted). Null for a job no agent sent (a web upload,
 * the copilot, a collect run's file), which carries no delivery.
 */
export function deliveryWords(job: Pick<ImportJob, "delivered_via" | "gateway_name">): string | null {
  if (job.delivered_via === "direct") return "Direct";
  if (job.delivered_via === "gateway") return job.gateway_name ? `Via ${job.gateway_name}` : "Via a gateway";
  return null;
}

/** Reprocess is offered as canReprocessJob says, never for a job whose file was sent (409 routed_to_documents), nor one
 *  another job of its file stands in place of (replaced_by: 409 file_has_another_job). */
export function offersReprocess(job: ImportJob): boolean {
  // A cancelled revision (#1428) is asked for again from its reader, not reprocessed (the server refuses it).
  return (
    canReprocessJob(job.status) &&
    !jobRouted(job) &&
    !job.replaced_by &&
    !(job.kind === "revision" && job.status === "cancelled")
  );
}

/** What a job's kind adds under its status (#1428): nothing for an import. */
export function jobKindText(job: Pick<ImportJob, "kind">): string {
  switch (job.kind) {
    case "revision":
      return "A revision of the reader in use, on this file";
    case "reapply":
      return "Read again with the reader's new version";
    default:
      return "";
  }
}

/** What a reprocess came to, for its toast: queued again, or sent, for a held file whose product the org now holds. */
export function reprocessMessage(job: ImportJob): string {
  return jobRouted(job) ? `sent to ${ROUTED_PRODUCT}` : "queued again";
}

/**
 * The documents lane's refusals of a reprocess (409), in plain words: the file was sent to another product, or it
 * waits for its product (the detail is the hold's reason). Null for any other failure, whose own words stand.
 */
export function reprocessRefusalText(err: unknown): string | null {
  if (!(err instanceof ApiClientError) || err.status !== 409) return null;
  const body = err.body as { error?: string; detail?: string } | null;
  if (body?.error === "routed_to_documents") return `sent to ${ROUTED_PRODUCT}, so it is not imported`;
  // R2 of docs/plans/2026-10-19_reader-layout-change.md: the file imports with its reader's update.
  if (body?.error === "file_has_revision") return "its reader is being updated for it; it imports once the update is approved";
  // PR8 of the 2026-10-23 plan: the server names when the file was imported again, or which job of it is running.
  if (body?.error === "file_has_another_job") return body.detail || "another job of this file stands; this one is not reprocessed";
  if (body?.error === "held_for_product") {
    if (body.detail) return lowerFirst(holdReasonText(body.detail));
    return "waiting for its product to be enabled for this organisation";
  }
  return null;
}

/** What became of an uploaded file, in one line for a toast: its job, or the product the documents lane sent it to. */
/** A process sheet's family (P6): every PDF is one, read for a grade's chemistry limits and never imported. */
export const SPEC_SHEET_FAMILY = "spec_sheet";

/** Whether a file is a PDF by its name: the Ready to send card asks for its grade. */
export function isPdfName(name: string): boolean {
  return name.toLowerCase().endsWith(".pdf");
}

export function uploadMessage(answer: ImportUploadAnswer, grade?: string): string {
  if (isRoutedUpload(answer) && answer.document.family === SPEC_SHEET_FAMILY) {
    const g = grade?.trim();
    return `${answer.document.file_name}: a process sheet${g ? ` for ${g}` : ""}, sent to be read. Review its draft in Products and tooling, under Process sheets.`;
  }
  if (isRoutedUpload(answer)) return `${answer.document.file_name}: sent to ${answer.document.product_name}`;
  const name = answer.file.filename;
  if (answer.status === "duplicate") return `${name}: already imported (the same file came in before)`;
  if (answer.status === "queued") return `${name}: queued for import`;
  // A document family's file from an org that does not hold the family's product is kept, never sent to the author.
  if (answer.status === "held" && answer.hold_reason) {
    return `${name}: kept, ${lowerFirst(holdReasonText(answer.hold_reason))}`;
  }
  return `${name}: already on its way (${jobStatusText(answer.status).toLowerCase()})`;
}

export interface ManifestStream {
  key: string;
  mode: string;
  kind?: string;
  equipment?: string;
  params?: { tag: string; equipment?: string; unit?: string; source?: string }[];
  /** Where the stream's file comes from, as its reader says: "history" or "report"; absent for none. */
  source_role?: string;
}

/**
 * Whether a stream's rows land on equipment its approval binds, as the server's `onEquipment` says: a lots stream's
 * (castings and their defects) and a products stream's (the parts the plant makes) land on none.
 */
export function landsOnEquipment(s: ManifestStream): boolean {
  return s.mode !== "lots" && s.mode !== "products";
}

/**
 * The equipment keys an approval must bind for one stream, as the server derives them: none for a lots or a
 * products stream; otherwise each param's equipment (its stream's when the param names none), or the stream's own
 * when it has no params.
 */
export function streamEquipmentKeys(s: ManifestStream): string[] {
  if (!landsOnEquipment(s)) return [];
  const set = new Set<string>();
  for (const p of s.params ?? []) set.add(p.equipment || s.equipment || "");
  if (set.size === 0 && s.equipment) set.add(s.equipment);
  return [...set].sort();
}

/** What the approval card says of a stream that binds no equipment: by its own mode ("lots: ...", "products: ..."). */
export function noEquipmentText(s: ManifestStream): string {
  return `${s.mode || "stream"}: binds no equipment`;
}

export type Bindings = Record<string, Record<string, string>>;

/** "stream: equipment" for every key still unbound, in manifest order. */
export function missingBindings(streams: ManifestStream[], chosen: Bindings): string[] {
  const out: string[] = [];
  for (const s of streams) {
    for (const eq of streamEquipmentKeys(s)) {
      if (!own(own(chosen, s.key), eq)) out.push(`${s.key}: ${eq}`);
    }
  }
  return out;
}

/**
 * The approval's body: every stream of the manifest, with exactly the keys it needs. `roles` is the reviewer's role for
 * a stream's data source where they chose one (one series per port, S1b: "history", "report" or null for none), sent
 * only on a stream whose rows land on equipment, as the server takes it; a stream with no choice sends none, so its
 * reader's role is written only where the data source has none.
 */
export function approveBody(
  streams: ManifestStream[],
  chosen: Bindings,
  roles: Readonly<Record<string, SourceRoleBody>> = {},
): ImportApproveRequest {
  const out: ImportApproveRequest = { streams: {} };
  for (const s of streams) {
    const b: Record<string, string> = {};
    for (const eq of streamEquipmentKeys(s)) b[eq] = own(own(chosen, s.key), eq) ?? "";
    out.streams[s.key] = { bindings: b };
    if (landsOnEquipment(s) && Object.prototype.hasOwnProperty.call(roles, s.key)) {
      out.streams[s.key].source_role = roles[s.key];
    }
  }
  return out;
}

/** A stream's role as the approval sends it: "history", "report", or null for none. */
export type SourceRoleBody = "history" | "report" | null;

/** An item of the org's plant an equipment key can be bound to: any kind, since a lab's tests land on an instrument. */
export interface BindOption {
  id: string;
  name: string;
  kind: string;
  templateId?: string;
}

/** The picker's groups, in this order, with the words it shows; a kind not listed here goes last, under "Other". */
export const BIND_KINDS: readonly { kind: string; label: string }[] = [
  { kind: "EQUIPMENT", label: "Equipment" },
  { kind: "INSTRUMENT", label: "Instruments and labs" },
  { kind: "STATION", label: "Stations and stores" },
  { kind: "TRANSPORT", label: "Transport" },
];

/** Every item, grouped by kind in BIND_KINDS order, each group by name; empty groups are left out. */
export function bindGroups<T extends { name: string; kind?: string }>(items: readonly T[]): { label: string; options: T[] }[] {
  const known = new Set(BIND_KINDS.map((k) => k.kind));
  const groups = [
    ...BIND_KINDS.map((k) => ({ label: k.label, options: items.filter((e) => e.kind === k.kind) })),
    { label: "Other", options: items.filter((e) => !known.has(e.kind ?? "")) },
  ];
  return groups
    .map((g) => ({ label: g.label, options: [...g.options].sort((a, b) => a.name.localeCompare(b.name)) }))
    .filter((g) => g.options.length > 0);
}

/** The words of a key, a name or a template id: lower case, split on anything but letters and digits. */
function wordsOf(s: string): string[] {
  return s
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

/** One word stands for another when they are equal, or one starts the other and is 3 letters or more (lab, laboratory). */
function sameWord(a: string, b: string): boolean {
  if (a === b) return true;
  const [short, long] = a.length <= b.length ? [a, b] : [b, a];
  return short.length >= 3 && long.startsWith(short);
}

/**
 * The item an equipment key most likely means, or null when none clearly does. Every word of the key must match a
 * word of the item's name or template (sand_lab: Sand Laboratory); among those, the item whose name carries the
 * fewest other words wins, and a tie is null: the reviewer chooses, nothing is guessed. A suggestion is never a new
 * item, only one already in the plant.
 */
export function suggestEntity(key: string, items: readonly BindOption[]): string | null {
  const want = wordsOf(key);
  if (want.length === 0) return null;
  let best: { id: string; extra: number } | null = null;
  let tied = false;
  for (const e of items) {
    const name = wordsOf(e.name);
    const all = [...name, ...wordsOf(e.templateId ?? "")];
    if (!want.every((w) => all.some((x) => sameWord(w, x)))) continue;
    const extra = name.filter((x) => !want.some((w) => sameWord(w, x))).length;
    if (!best || extra < best.extra) {
      best = { id: e.id, extra };
      tied = false;
    } else if (extra === best.extra) {
      tied = true;
    }
  }
  return best && !tied ? best.id : null;
}

export interface Chip {
  tone: BadgeVariant;
  text: string;
  /** What the chip leaves out, for its hover: a finished run's warnings, one per line. */
  detail?: string;
}

function reasonWord(job: AnalysisJob): string {
  const kinds = new Set(job.reasons.map((r) => r.split(":")[0]));
  if (kinds.has("import")) return "after an import";
  if (kinds.has("import_undo")) return "after an undo";
  if (kinds.has("plant_time")) return "after a plant-time change";
  if (kinds.has("manual")) return "on request";
  return kinds.size ? `after ${[...kinds][0].replace(/_/g, " ")}` : "";
}

/**
 * Where the org's analysis stands, from its newest jobs. A cancelled job is a failed attempt whose reasons
 * moved into a newer queued one, so the newest job that is not cancelled speaks.
 */
export function analysisChip(jobs: AnalysisJob[]): Chip {
  const job = jobs.find((j) => j.status !== "cancelled") ?? jobs[0];
  if (!job) return { tone: "secondary", text: "Analysis: not run yet" };
  const why = reasonWord(job);
  switch (job.status) {
    case "queued":
      return { tone: "default", text: `Analysis: queued${why ? ` (${why})` : ""}` };
    case "running":
      return { tone: "info", text: "Analysis: running" };
    case "done": {
      // A step that completed can still have skipped items (a tag that failed to profile): not silent.
      const warnings = Object.entries(job.steps ?? {})
        .sort(([, a], [, b]) => a.order - b.order)
        .flatMap(([name, st]) => (st.warnings ?? []).map((w) => `${name}: ${w}`));
      if (warnings.length === 0) return { tone: "success", text: "Analysis: up to date" };
      return {
        tone: "warning",
        text: `Analysis: up to date, ${warnings.length} warning${warnings.length === 1 ? "" : "s"}`,
        detail: warnings.join("\n"),
      };
    }
    case "failed":
      return { tone: "danger", text: `Analysis failed: ${job.error || "no reason recorded"}` };
    default:
      return { tone: "secondary", text: "Analysis: cancelled" };
  }
}
